import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { GuideLinks } from "@/components/GuideLinks";
import { RoiCalculator } from "@/components/RoiCalculator";
import { LeadForm } from "@/components/LeadForm";
import { StickyCta } from "@/components/StickyCta";
import { CaseStrip } from "@/components/CasePreview";

const SITE = "https://shopify-freelancer.net";
const TITLE = "Shopify Conversion Optimierung | Festpreis ab 3.990 €";
const DESCRIPTION =
  "Shopify Conversion Optimierung vom Freelancer: mehr Umsatz aus dem Traffic, den Sie schon haben. Audit, Umsetzung im Theme, A/B-Tests, Festpreis ab 3.990 € netto.";

const faqs = [
  {
    q: "Was ist Conversion Optimierung überhaupt?",
    a: "Besucher kommen in Ihren Shop, aber zu wenige bestellen? Ich prüfe Produktseiten, Warenkorb, mobile Nutzung und Ladezeiten und setze die wichtigsten Verbesserungen um. Der Traffic bleibt gleich, das Ziel ist ein besserer Kaufabschluss.",
  },
  {
    q: "Bringen Anzeigen allein mehr Umsatz?",
    a: "Ehrlich gesagt: Anzeigen ohne guten Shop verbrennen Budget. Google Ads oder Microsoft Ads kaufen Besucher, aber nur ein Shop, der überzeugt, macht daraus Käufer. Deshalb optimiere ich zuerst die Kaufabschlussrate und berate Sie danach, welche Besucherquellen sich rechnen.",
  },
  {
    q: "Wie lange dauert das Projekt?",
    a: "Ein Sprint ist typischerweise auf vier Wochen angelegt: Audit, Umsetzung, Auswertung. Wie schnell Ergebnisse belastbar sind, hängt von Ihrem Traffic ab. Bei wenig Bestellungen dauert es länger, bis Zahlen aussagekräftig sind.",
  },
  {
    q: "Muss mein Store neu gebaut werden?",
    a: "Nein. Ich arbeite in Ihrem bestehenden Theme. Nur wenn das Theme technisch am Ende ist, sprechen wir über einen Umzug, und zwar vor Projektstart.",
  },
  {
    q: "Ab wann lohnt sich das?",
    a: "Das hängt von Traffic, heutiger Conversion-Rate, Warenkorb, Marge und dem nötigen Aufwand ab. Ich prüfe vorab, ob sich die Arbeit für Ihren Shop überhaupt lohnt, und sage Ihnen offen, wenn nicht.",
  },
  {
    q: "Wie viel Steigerung ist realistisch?",
    a: "Das lässt sich vorab nicht seriös beziffern, weil es von Ausgangslage, Sortiment und Traffic abhängt. Was ich zusage: klare Messgrößen vor dem Start und eine ehrliche Auswertung danach, auch wenn ein Test nichts bringt.",
  },
  {
    q: "Kann der Checkout angepasst werden?",
    a: "Nur im Rahmen dessen, was Shopify freigibt. Welche Eingriffe möglich sind, hängt von Ihrem Shopify-Plan und den verfügbaren Erweiterungspunkten ab. Vieles, was Abbrüche verursacht, liegt ohnehin vor dem Checkout, etwa Versandkosten, Zahlarten und Vertrauen auf der Produktseite.",
  },
  {
    q: "Woran messen wir den Erfolg?",
    a: "An Kaufabschlussrate gesamt und mobil, Add-to-Cart-Rate, Checkout-Abbrüchen, Warenkorbhöhe und vor allem am Umsatz pro Session. Diese Werte stehen vor dem Start schriftlich fest.",
  },
];

export const Route = createFileRoute("/shopify-conversion-optimierung")({
  component: ConversionPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/shopify-conversion-optimierung` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/shopify-conversion-optimierung` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Shopify Conversion Optimierung",
          description: DESCRIPTION,
          serviceType: "Shopify Conversion Rate Optimierung",
          areaServed: "DACH",
          offers: {
            "@type": "AggregateOffer",
            lowPrice: "3990",
            highPrice: "18900",
            offerCount: 3,
            priceCurrency: "EUR",
            name: "Conversion-Pakete für Shopify Stores",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});

const problems = [
  "Traffic kommt, aber der Warenkorb bleibt leer",
  "Ads werden teurer, der ROAS wird schlechter",
  "Theme wurde nie für Kaufentscheidungen gebaut",
  "Auf dem Smartphone brechen spürbar mehr Besucher ab als am Desktop",
];

const steps = [
  {
    no: "01",
    title: "Conversion-Audit",
    text: "Analyse von Analytics, Heatmaps, Checkout-Funnel und Mobile-Erlebnis. Ich zeige Ihnen schwarz auf weiß, wo Ihr Umsatz verloren geht.",
  },
  {
    no: "02",
    title: "Umsetzung im Theme",
    text: "Produktseiten, Kollektionen, Warenkorb und Checkout werden neu strukturiert, sauber im Code, ohne Speed-Verlust und ohne App-Friedhof.",
  },
  {
    no: "03",
    title: "Testen & Nachschärfen",
    text: "Tests auf den Hebeln mit dem größten Hebel, soweit Ihre Datenmenge das zulässt. Danach Auswertung und Empfehlung für den nächsten Schritt.",
  },
];

const principles = [
  {
    title: "Sie kaufen ein Ergebnis",
    text: "Mehr Umsatz aus dem Traffic, den Sie heute schon einkaufen. Festpreis, definierter Zielzustand, klarer Endpunkt, Ihr Budget ist am ersten Tag bekannt.",
  },
  {
    title: "Die Kaufabschlussrate entscheidet",
    text: "Zielbild und Messgrößen stehen vor dem ersten Handgriff schriftlich fest. Danach ist jede Änderung belegbar, auch gegenüber Geschäftsführung und Beirat.",
  },
  {
    title: "Erst Diagnose, dann Eingriff",
    text: "Analytics, Funnel und Mobile-Erlebnis liefern die Reihenfolge. Sie bekommen die zwei bis drei Hebel mit dem größten Umsatzeffekt zuerst, nicht die längste Maßnahmenliste.",
  },
  {
    title: "Sie erfahren es vorher",
    text: "Rechnet sich ein Sprint für Ihren Shop nicht, hören Sie das vor dem Angebot. Das schützt Ihr Budget und macht die Empfehlung, die kommt, belastbar.",
  },
];

function ConversionPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />

      <main id="top">
        <section className="veil border-b border-border/60 py-16 md:py-24">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 md:grid-cols-2 md:items-center">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
                Shopify · Conversion Optimierung
              </p>
              <h1 className="mt-5 text-4xl font-bold leading-[1.05] md:text-6xl">
                Mehr Umsatz aus dem Traffic, den Ihr Shop{" "}
                <span className="text-signal">schon heute</span> hat.
              </h1>
              <p className="mt-6 max-w-xl text-lg text-muted-foreground">
                Besucher kommen in Ihren Shop, aber zu wenige bestellen? Ich prüfe
                Produktseiten, Warenkorb, mobile Nutzung und Ladezeiten und setze die
                wichtigsten Verbesserungen um. Festpreis ab 3.990 € netto.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#anfrage"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                >
                  Kostenlose Einschätzung erhalten <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="#rechner"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold transition-colors hover:border-primary"
                >
                  Potenzial berechnen
                </a>
              </div>
            </div>
            <div id="anfrage">
              <LeadForm
                source="lp-conversion"
                defaultService="Conversion-Optimierung"
                showServices
                headline="Umsatzpotenzial einschätzen lassen"
                messagePlaceholder="Wie viele Besucher und Bestellungen hat Ihr Shop ungefähr im Monat?"
              />
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 md:grid-cols-[1fr_1.1fr]">
            <h2 className="text-3xl font-bold leading-tight md:text-4xl">
              Woran es im Shop meistens hakt.
              <br />
              Und was ich mir zuerst ansehe.
            </h2>
            <ul className="space-y-px overflow-hidden rounded-lg border border-border">
              {problems.map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-3 border-b border-border bg-card px-5 py-4 text-sm last:border-b-0 md:text-base"
                >
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <CaseStrip
          slugs={["deinlunu", "kremka"]}
          title="Conversion-Arbeit an echten Shops"
          intro="Zwei Projekte mit den vorhandenen Vorher/Nachher-Werten aus den genannten Zeiträumen. Projektspezifische Beobachtungen, kein isolierter A/B-Nachweis."
        />

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">So läuft es ab</h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {steps.map((s) => (
                <div key={s.no} className="shadow-lift rounded-lg border border-border bg-card p-6">
                  <span className="text-signal font-display text-4xl font-bold">{s.no}</span>
                  <h3 className="mt-4 text-lg font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>


        <section id="rechner" className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-bold md:text-4xl">Rechnen Sie nach, bevor wir reden</h2>
              <p className="mt-4 text-muted-foreground">
                Conversion-Optimierung ist keine Geschmacksfrage, sondern eine Rechenaufgabe.
                Tragen Sie Traffic, aktuelle Conversion-Rate und Warenkorb ein, und sehen Sie im
                Diagramm, was ein Prozentpunkt mehr Kaufabschlussrate für Ihren Umsatz
                bedeutet.
              </p>
            </div>
            <div className="mt-9">
              <RoiCalculator />
            </div>
            <div className="mt-8 flex flex-col gap-4 rounded-xl border border-primary/30 bg-card p-6 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Sieht die Zahl gut aus? Dann prüfe ich kostenlos, ob Ihr Shop dieses
                Potenzial wirklich hat.
              </p>
              <Link
                to="/kontakt"
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Potenzial prüfen lassen <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <div className="grid gap-10 md:grid-cols-[1fr_1.2fr]">
              <div>
                <h2 className="text-3xl font-bold leading-tight md:text-4xl">
                  Was Sie bekommen <br />
                  und woran Sie es messen
                </h2>
                <p className="mt-5 leading-relaxed text-muted-foreground">
                  Der Traffic ist bereits bezahlt. Meine Arbeit setzt an der Stelle an, an
                  der aus Besuchern Käufer werden: definierter Zielzustand, Festpreis,
                  messbarer Effekt auf Umsatz pro Besucher.
                </p>
                <Link
                  to="/methode"
                  className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary"
                >
                  Die komplette Methode ansehen <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {principles.map((p) => (
                  <div key={p.title} className="rounded-lg border border-border bg-card p-6">
                    <h3 className="text-base font-semibold">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="preis" className="veil border-b border-border/60 py-20">
          <div className="mx-auto max-w-4xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">Honorar</p>
            <h2 className="mt-5 text-3xl font-bold md:text-4xl">
              Drei Festpreise. Sie wählen die Tiefe, nicht das Ob.
            </h2>
            <div className="mt-10 divide-y divide-border/60 border-y border-border/60">
              {[
                {
                  name: "Sprint",
                  price: "3.990 €",
                  duration: "4 Wochen",
                  line: "Die offensichtlichen Umsatzverluste abstellen.",
                },
                {
                  name: "Programm",
                  price: "9.900 €",
                  duration: "3 Monate",
                  line: "Aus einmaliger Optimierung wird ein Testrhythmus.",
                },
                {
                  name: "Partnerschaft",
                  price: "18.900 €",
                  duration: "6 Monate",
                  line: "Laufende Verantwortung für Umsatz pro Besucher.",
                },
              ].map((t) => (
                <div
                  key={t.name}
                  className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 py-5"
                >
                  <div>
                    <span className="font-display text-xl font-bold">{t.name}</span>
                    <span className="ml-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                      {t.duration}
                    </span>
                    <p className="mt-1 text-sm text-muted-foreground">{t.line}</p>
                  </div>
                  <span className="font-display text-2xl font-bold tabular-nums">{t.price}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
              Alle Pakete netto, Umfang vorab schriftlich. Kleinere Aufgaben außerhalb der
              Pakete rechne ich mit 110 € netto pro Stunde ab.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Kostenlose Einschätzung erhalten <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/preise"
                className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3.5 text-sm font-semibold transition-colors hover:border-primary"
              >
                Was in welchem Paket steckt <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Häufige Fragen</h2>
            <div className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border">
              {faqs.map((f) => (
                <div key={f.q} className="bg-card p-6">
                  <h3 className="text-base font-semibold">{f.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="kontakt" className="py-20 pb-28 lg:pb-20">
          <div className="mx-auto grid max-w-5xl gap-10 px-5 md:grid-cols-2 md:items-center">
            <div>
              <h2 className="text-3xl font-bold md:text-4xl">
                Lassen Sie uns Ihren Store <span className="text-signal">durchrechnen</span>.
              </h2>
              <p className="mt-5 text-muted-foreground">
                Schicken Sie mir Ihre Shop-URL. Sie bekommen innerhalb von 48 Stunden eine ehrliche
                Einschätzung, kostenlos und ohne Verkaufsgespräch. Wenn sich ein Sprint
                für Sie nicht rechnet, sage ich das vorher.
              </p>
            </div>
            <LeadForm
              source="lp-conversion"
              compact={false}
              headline="Umsatzpotenzial einschätzen lassen"
              cta="Kostenlose Einschätzung erhalten"
            />
          </div>
        </section>
      </main>

      <StickyCta targetId="anfrage" />
      <GuideLinks
        links={[
        { to: "/blog/shopify-checkout-optimieren", label: "Checkout optimieren", text: "Wo im Kaufabschluss der Umsatz liegen bleibt." },
        { to: "/ratgeber/shopify-produktseite-optimieren", label: "Produktseite optimieren", text: "Der Aufbau, der Zweifel vor dem Kauf abbaut." },
        { to: "/ratgeber/shopify-mobile-optimierung", label: "Mobile Optimierung", text: "Warum mobil die meisten Bestellungen verloren gehen." },
        ]}
      />
      <SiteFooter />
    </div>
  );
}
